/** React Query keys and hooks, so every screen caches and invalidates the same way. */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { partnerApi } from '@/api/endpoints'
import { describeError } from '@/lib/errors'
import { toast } from '@/store/toast'
import type { CommissionStatus, Coupon } from '@/types/api'

export const keys = {
  dashboard: (days: number) => ['partner', 'dashboard', days] as const,
  coupons: ['partner', 'coupons'] as const,
  referrals: (page: number, pageSize: number) => ['partner', 'referrals', page, pageSize] as const,
  commissions: (status: string, page: number) => ['partner', 'commissions', status, page] as const,
  settlements: ['partner', 'settlements'] as const,
  me: ['partner', 'me'] as const,
}

export const useDashboard = (days: number) => useQuery({ queryKey: keys.dashboard(days), queryFn: () => partnerApi.dashboard(days), placeholderData: (previous) => previous })
export const useCoupons = () => useQuery({ queryKey: keys.coupons, queryFn: partnerApi.coupons })
export const useReferrals = (page: number, pageSize: number) =>
  useQuery({ queryKey: keys.referrals(page, pageSize), queryFn: () => partnerApi.referrals({ page, pageSize }), placeholderData: (previous) => previous })
export const useCommissions = (status: CommissionStatus | '', page: number, pageSize: number) =>
  useQuery({ queryKey: keys.commissions(status, page), queryFn: () => partnerApi.commissions({ status, page, pageSize }), placeholderData: (previous) => previous })
export const useSettlements = () => useQuery({ queryKey: keys.settlements, queryFn: partnerApi.settlements })

/** Put a changed coupon into the cached list, and let the dashboard recount. */
export function useCouponMutation<A>(fn: (args: A) => Promise<Coupon | void>, opts: { success?: (coupon: Coupon | void) => string; errorTitle?: string } = {}) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: fn,
    onSuccess: (coupon) => {
      if (coupon) queryClient.setQueryData<Coupon[]>(keys.coupons, (list) => (list ? (list.some((c) => c.id === coupon.id) ? list.map((c) => (c.id === coupon.id ? coupon : c)) : [coupon, ...list]) : list))
      void queryClient.invalidateQueries({ queryKey: keys.coupons })
      void queryClient.invalidateQueries({ queryKey: ['partner', 'dashboard'] })
      if (opts.success) toast.success(opts.success(coupon))
    },
    onError: (error) => {
      const { title, description } = describeError(error, opts.errorTitle)
      toast.error(title, description)
    },
  })
}
